import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Spinner,
  Alert,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react';
import { WarningIcon } from '@chakra-ui/icons';
import api from '../services/api';

const PreviewModal = ({ isOpen, onClose, fileId }) => {
  const [previewData, setPreviewData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfUrl, setPdfUrl] = useState(null);

  // Oprydning af blob URL når komponenten unmountes eller når en ny fil vises
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  useEffect(() => {
    const fetchPreview = async () => {
      if (!fileId) return;
      
      try {
        setLoading(true);
        setError(null);
        // Ryd tidligere blob URL
        if (pdfUrl) {
          URL.revokeObjectURL(pdfUrl);
          setPdfUrl(null);
        }

        const data = await api.get(`/files/preview/${fileId}`);
        setPreviewData(data);

        // Opret blob URL for PDF
        if (data.type === 'pdf') {
          const binaryData = atob(data.content);
          const array = new Uint8Array(binaryData.length);
          for (let i = 0; i < binaryData.length; i++) {
            array[i] = binaryData.charCodeAt(i);
          }
          const blob = new Blob([array], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);
          setPdfUrl(url);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileId, isOpen]);

  const renderContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minH="200px">
          <Spinner size="xl" />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert status="error">
          <WarningIcon />
          {error}
        </Alert>
      );
    }

    if (!previewData) {
      console.warn('Ingen preview data tilgængelig');
      return null;
    }

    switch (previewData.type) {
      case 'text':
        return (
          <Box whiteSpace="pre-wrap" p={4} fontFamily="monospace">
            {previewData.content}
          </Box>
        );

      case 'pdf':
        return (
          <Box height="80vh">
            <iframe
              src={`data:application/pdf;base64,${previewData.content}`}
              style={{
                width: '100%',
                height: '100%',
                border: 'none'
              }}
              title="PDF Preview"
            />
          </Box>
        );

      case 'spreadsheet':
        return (
          <Box overflowX="auto">
            <TableContainer>
              <Table variant="simple">
                <Thead>
                  <Tr>
                    {previewData.content[0]?.map((header, idx) => (
                      <Th key={idx}>{header || `Kolonne ${idx + 1}`}</Th>
                    ))}
                  </Tr>
                </Thead>
                <Tbody>
                  {previewData.content.slice(1).map((row, rowIdx) => (
                    <Tr key={rowIdx}>
                      {row.map((cell, cellIdx) => (
                        <Td key={cellIdx}>{cell}</Td>
                      ))}
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
        );

      case 'document':
        return (
          <Box p={4} className="document-preview" 
               dangerouslySetInnerHTML={{ __html: previewData.content }} />
        );

      default:
        console.warn('Ukendt filtype:', previewData.type);
        return (
          <Alert status="warning">
            <WarningIcon />
            Denne filtype understøttes ikke til preview
          </Alert>
        );
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent maxW="90vw" maxH="90vh">
        <ModalHeader>
          {previewData?.filename || 'Forhåndsvisning'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody overflow="auto">
          {renderContent()}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default PreviewModal; 
 